import React, { useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/styles";
import {
  IconButton,
  FormControl,
  Select,
  MenuItem,
} from "@material-ui/core";
import { Menu, ArrowDropUpSharp, Search } from "@material-ui/icons";
import MyLocationIcon from "@material-ui/icons/MyLocation";
import ViewListIcon from "@material-ui/icons/ViewList";
import MapDrawer from "./drawer/MapDrawer";
import MapSearchPanel from "./search/MapSearchPanel";
import CampusNameDisplay from "./CampusNameDisplay";
import campusConfig from "../../json/campus_config.json";
import { useLanguage, localizedField } from "../../contexts/LanguageContext";

/** displaySettings から初期状態オブジェクトを生成（全てtrue） */
function buildInitialDisplayChecked() {
  const settings = campusConfig.displaySettings || [];
  return settings.reduce((acc, s) => ({ ...acc, [s.id]: true }), {});
}

const useStyles = makeStyles((theme) => ({
  toolsBarLeft: {
    display: "flex",
    justifyContent: "left",
    flexDirection: "column",
    position: "absolute",
    left: 0,
    bottom: 0,
    zIndex: 100,
    height: "auto",
  },
  toolsBarRight: {
    display: "flex",
    justifyContent: "right",
    flexDirection: "column",
    position: "absolute",
    right: 0,
    bottom: 0,
    zIndex: 100,
    height: "auto",
    pointerEvents: "none",
  },
  toolsBarTop: {
    position: "absolute",
    bottom: 10,
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    zIndex: 100,
    width: "100%",
    height: "auto",
    display: "flex",
    justifyContent: "center",
    boxSizing: "border-box",
  },
  menuBarContainer: {
    display: "flex",
    width: "100%",
  },
  menuBarLeft: {
    display: "flex",
    alignItems: "center",
  },
  menuBarCenter: {
    display: "flex",
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  menuBarRight: {
    display: "flex",
    alignItems: "center",
  },
  centerSelector: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "white",
    borderRadius: 24,
    height: 32,
    padding: theme.spacing(0.5, 2),
    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
  },
  overviewButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.04)",
      borderRadius: 18,
    },
    "@media (hover: none)": {
      "&:hover": {
        backgroundColor: "white",
      },
    },
    "& svg": {
      fontSize: 20,
      color: "#666",
    },
  },
  selectorDivider: {
    width: 1,
    height: 30,
    backgroundColor: "#d0d0d0",
    margin: theme.spacing(0, 1),
  },
  campusMenuPaper: {
    borderRadius: 18,
    marginTop: theme.spacing(1),
    boxShadow: "0 10px 30px rgba(0,0,0,0.16)",
    border: "1px solid rgba(0,0,0,0.06)",
  },
  campusMenuList: {
    padding: theme.spacing(1),
    "& .MuiMenuItem-root": {
      borderRadius: 12,
      margin: theme.spacing(0.5, 0),
      fontSize: 14,
    },
    "& .MuiMenuItem-root:hover": {
      backgroundColor: "rgba(0,0,0,0.04)",
    },
  },
  campusFormControl: {
    width: 140,
    overflow: "hidden",
  },
  campusSelectMenuBar: {
    width: "100%",
    border: "none",
    fontSize: 14,
    "& .MuiSelect-select": {
      padding: theme.spacing(0.5, 1),
      paddingRight: theme.spacing(4),
      overflow: "hidden",
    },
    "& .MuiSelect-select:focus": {
      backgroundColor: "transparent",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
  iconButton: {
    backgroundColor: "white",
    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
    width: 44,
    height: 44,
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
    "@media (hover: none)": {
      "&:hover": {
        backgroundColor: "white",
      },
    },
  },
  iconButtonActive: {
    backgroundColor: "#e8f0fe",
    "&:hover": {
      backgroundColor: "#dbe8fd",
    },
    "@media (hover: none)": {
      "&:hover": {
        backgroundColor: "#e8f0fe",
      },
    },
    "& svg": {
      color: "#1a73e8",
    },
  },
  roomControl: {
    position: "absolute",
    textAlign: "left",
    left: theme.spacing(2),
    bottom: theme.spacing(10),
  },
  zoomControl: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    position: "absolute",
    bottom: "80px",
    right: "16px",
    gap: "8px",
    pointerEvents: "auto",
  },
  paper: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    width: "100%",
  },
}));

function MapToolbar({
  listArray,
  handleClick,
  handleCenterOnGps,
  handleResetView,
  changeCampus,
  currentCampus,
  children,
  useGps,
  isGpsAvailable,
  northIndicatorChecked,
  onNorthIndicatorChange,
}) {
  const classes = useStyles();
  const { language } = useLanguage();
  const [dataJson, setDataJson] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [displayChecked, setDisplayChecked] = useState(
    buildInitialDisplayChecked,
  );
  const [searchOpen, setSearchOpen] = useState(false);

  const handleDisplayCheckedChange = useCallback((id, checked) => {
    setDisplayChecked((prev) => ({ ...prev, [id]: checked }));
  }, []);

  useEffect(() => {
    async function fetchData() {
      const data = await import(`../../json/campus_data.json`);
      setDataJson(data.default.campusList);
    }
    fetchData();
  }, []);

  useEffect(() => {
    setDisplayChecked(buildInitialDisplayChecked());
  }, [currentCampus]);

  useEffect(() => {
    setSearchOpen(false);
  }, [language]);

  if (dataJson.length === 0) return null;

  return (
    <>
      <div className={classes.toolsBarTop}>
        <div className={classes.menuBarContainer}>
          <div className={classes.menuBarLeft}>
            <IconButton
              className={classes.iconButton}
              onClick={() => setDrawerOpen(true)}
              size="small"
            >
              <Menu />
            </IconButton>
          </div>
          <div className={classes.menuBarCenter}>
            <div className={classes.centerSelector}>
              <IconButton
                className={classes.overviewButton}
                onClick={handleResetView}
                size="small"
              >
                <ViewListIcon />
              </IconButton>
              <div className={classes.selectorDivider} />
              <FormControl className={classes.campusFormControl}>
                <Select
                  className={classes.campusSelectMenuBar}
                  value={currentCampus}
                  variant="outlined"
                  IconComponent={ArrowDropUpSharp}
                  renderValue={(value) => {
                    const campus = dataJson[value - 1];
                    if (!campus) return null;
                    const name = localizedField(campus, "label", language);
                    return (
                      <CampusNameDisplay name={name} />
                    );
                  }}
                  MenuProps={{
                    classes: { paper: classes.campusMenuPaper },
                    MenuListProps: { className: classes.campusMenuList },
                    getContentAnchorEl: null,
                    anchorOrigin: { vertical: "top", horizontal: "center" },
                    transformOrigin: {
                      vertical: "bottom",
                      horizontal: "center",
                    },
                  }}
                  onChange={(e) => {
                    changeCampus(e.target.value);
                  }}
                >
                  {dataJson.map((c, i) => (
                    <MenuItem key={c.code} value={i + 1}>
                      {localizedField(c, "label", language)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          <div className={classes.menuBarRight}>
            <IconButton
              className={`${classes.iconButton} ${
                searchOpen ? classes.iconButtonActive : ""
              }`}
              aria-pressed={searchOpen}
              onClick={() => setSearchOpen((prev) => !prev)}
              size="small"
            >
              <Search />
            </IconButton>
          </div>
        </div>
        {/* 展開されたドロワー */}
        <MapDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          displayChecked={displayChecked}
          onDisplayCheckedChange={handleDisplayCheckedChange}
          northIndicatorChecked={northIndicatorChecked}
          onNorthIndicatorChange={onNorthIndicatorChange}
        />
      </div>

      <MapSearchPanel
        open={searchOpen}
        listArray={listArray}
        onSelect={handleClick}
        resetKey={`${currentCampus}:${language}`}
      />

      <div className={classes.toolsBarLeft}>
        <div className={classes.roomControl}>{children}</div>
      </div>

      <div className={classes.toolsBarRight}>
        <div className={classes.zoomControl}>
          {useGps && isGpsAvailable && (
            <IconButton
              aria-label="gps"
              className={classes.iconButton}
              size="small"
              onClick={handleCenterOnGps}
            >
              <MyLocationIcon />
            </IconButton>
          )}
        </div>
      </div>
    </>
  );
}

export default React.memo(MapToolbar);
